import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Spin } from 'antd'
import { useDriverDataContext } from '../../../../driverData/context/DriverDataContext'
import { parseFormula } from '../../../utils/formulaCalculation'
import CodeBlock from '../../../../../components/Misc/CodeBlock'
import { formatValueByType } from '../../../../../utils/helpers'
import { VariableType } from '../../../types'

interface FormulaCalculationTooltipProps {
  formula: string
  /** Fiscal year mode: only show formula, no driver values */
  fiscalYearMode?: boolean
  periodYear?: number
  periodMonth?: number
  dimensionId?: string | null
}

const TooltipContainer = styled.div`
  max-width: 400px;
  padding: 8px 0;
`

const Section = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.85);
`

const FormulaCodeWrapper = styled.div`
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;

  pre {
    margin: 0;
    background: transparent;
    padding: 0;
    font-size: 11px;
    line-height: 1.5;
  }
`

const VariableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const VariableItem = styled.div<{ $missing?: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: 3px 6px;
  background: ${props => (props.$missing ? '#fff2e8' : '#f5f5f5')};
  border-radius: 3px;
  font-size: 12px;
`

const VariableName = styled.span`
  font-weight: 500;
  color: rgba(0, 0, 0, 0.65);
`

const VariableValue = styled.span<{ $missing?: boolean }>`
  color: ${props => (props.$missing ? '#ff4d4f' : 'rgba(0, 0, 0, 0.85)')};
`

const ErrorText = styled.div`
  color: #ff4d4f;
  padding: 8px;
  background: #fff2f0;
  border-radius: 4px;
  font-size: 13px;
`

/**
 * Tooltip content showing formula calculation details
 * Uses DriverDataContext to access driver data without creating multiple observers
 */
export const FormulaCalculationTooltip: React.FC<FormulaCalculationTooltipProps> = ({
  formula,
  fiscalYearMode,
  periodYear,
  periodMonth,
  dimensionId
}) => {
  const { t } = useTranslation()
  const { driverData, budgetDriverMap, isLoading } = useDriverDataContext()

  const parsed = useMemo(() => {
    // In fiscal year mode, don't parse driver values - just show formula
    if (fiscalYearMode) {
      return { rawFormula: formula, variables: [], error: null }
    }

    if (isLoading || !driverData.length || periodYear === undefined || periodMonth === undefined) return null

    return parseFormula(formula, driverData, budgetDriverMap, dimensionId ?? null, periodYear, periodMonth)
  }, [formula, fiscalYearMode, driverData, budgetDriverMap, dimensionId, periodYear, periodMonth, isLoading])

  if (!fiscalYearMode && isLoading) {
    return (
      <TooltipContainer>
        <Spin size="small" />
      </TooltipContainer>
    )
  }

  if (!parsed) {
    return (
      <TooltipContainer>
        <ErrorText>{t('financialStatementsPage:unableToParseFormula')}</ErrorText>
      </TooltipContainer>
    )
  }

  const { rawFormula, variables, error } = parsed

  return (
    <TooltipContainer>
      <Section>
        {/* Don't show section title in fiscal year mode - Popover already has title */}
        {!fiscalYearMode && <SectionTitle>{t('financialStatementsPage:formula')}</SectionTitle>}
        <FormulaCodeWrapper>
          <CodeBlock code={rawFormula} />
        </FormulaCodeWrapper>
      </Section>

      {/* Only show driver values in monthly mode (not fiscal year mode) */}
      {!fiscalYearMode && variables.length > 0 && (
        <Section>
          <SectionTitle>{t('financialStatementsPage:driverValues')}</SectionTitle>
          <VariableList>
            {variables.map(variable => {
              const isMissing = variable.value === null

              // Get formatting info from budget driver's formula
              const formulaType = variable.budgetDriver?.formula?.type as VariableType | undefined
              const decimals = variable.budgetDriver?.formula?.style?.decimals

              // Format value using the same logic as cells
              const formattedValue = isMissing
                ? t('financialStatementsPage:notAvailable')
                : formatValueByType(
                    variable.value!,
                    formulaType,
                    decimals !== undefined
                      ? { maximumFractionDigits: decimals, minimumFractionDigits: decimals }
                      : undefined
                  )

              return (
                <VariableItem key={variable.formulaId} $missing={isMissing}>
                  <VariableName>
                    {variable.driverName || variable.name}
                    {isMissing && ` (${t('financialStatementsPage:missing')})`}
                  </VariableName>
                  <VariableValue $missing={isMissing}>{formattedValue}</VariableValue>
                </VariableItem>
              )
            })}
          </VariableList>
        </Section>
      )}

      {error && (
        <Section>
          <ErrorText>{error}</ErrorText>
        </Section>
      )}
    </TooltipContainer>
  )
}
